import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Calendar, Car, X, Navigation, Loader2 } from 'lucide-react';
import { Drawer, DrawerContent, DrawerTrigger, DrawerClose, DrawerPortal, DrawerOverlay } from '@/components/ui/drawer';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useCategories } from '@/hooks/useCategories';

interface Location {
  id: string;
  name: string;
  slug: string;
}

let cachedMapboxToken: string | null = null;

export const MobileSearchSheet: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState('');
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [listingType, setListingType] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  
  const { eventCategories, experienceCategories } = useCategories();

  useEffect(() => {
    fetchLocations();
    loadUserLocation();
  }, []);

  const fetchLocations = async () => {
    const { data } = await supabase
      .from('locations')
      .select('id, name, slug')
      .eq('is_active', true)
      .order('display_order')
      .limit(10);
    
    if (data) setLocations(data);
  };

  const loadUserLocation = () => {
    const cachedCity = localStorage.getItem('userCity');
    if (cachedCity) {
      setUserCity(cachedCity);
    }
  };

  const getMapboxToken = async (): Promise<string | null> => {
    if (cachedMapboxToken) return cachedMapboxToken;
    
    try {
      const { data, error } = await supabase.functions.invoke('get-mapbox-token');
      if (error || !data?.token) return null;
      cachedMapboxToken = data.token;
      return data.token;
    } catch {
      return null;
    }
  };

  const handleNearbyClick = async () => {
    if (userCity) {
      setLocation(userCity);
      return;
    }

    setNearbyLoading(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const token = await getMapboxToken();
            if (!token) {
              setNearbyLoading(false);
              setLocation('Nearby');
              return;
            }

            const response = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${position.coords.longitude},${position.coords.latitude}.json?access_token=${token}&types=place`
            );
            const data = await response.json();
            if (data.features && data.features.length > 0) {
              const city = data.features[0].text;
              localStorage.setItem('userCity', city);
              localStorage.setItem('userLat', position.coords.latitude.toString());
              localStorage.setItem('userLon', position.coords.longitude.toString());
              setUserCity(city);
              setNearbyLoading(false);
              setLocation(city);
            } else {
              setNearbyLoading(false);
              setLocation('Nearby');
            }
          } catch {
            setNearbyLoading(false);
            setLocation('Nearby');
          }
        },
        () => {
          setNearbyLoading(false);
          setLocation('Nearby');
        }
      );
    } else {
      setNearbyLoading(false);
      setLocation('Nearby');
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (location) params.set('location', location);
    if (date) params.set('date', format(date, 'yyyy-MM-dd'));
    if (listingType && listingType !== 'all') params.set('type', listingType);
    if (selectedCategory) params.set('category', selectedCategory);
    
    // Check if using nearby for distance sorting
    const userLat = localStorage.getItem('userLat');
    const userLon = localStorage.getItem('userLon');
    if (userLat && userLon && (location === userCity || location === 'Nearby')) {
      params.set('nearby', 'true');
      params.set('lat', userLat);
      params.set('lon', userLon);
    }
    
    setOpen(false);
    navigate(`/search?${params.toString()}`);
  };

  const handleClear = () => {
    setLocation('');
    setDate(undefined);
    setListingType('all');
    setSelectedCategory(null);
  };

  const categories = listingType === 'events' 
    ? eventCategories 
    : listingType === 'experiences' 
      ? experienceCategories 
      : [];

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button className="flex items-center gap-3 w-full px-4 py-3 bg-background border border-border rounded-full shadow-sm">
          <Search className="w-5 h-5 text-muted-foreground" />
          <span className="text-muted-foreground text-sm">Where are you going?</span>
        </button>
      </DrawerTrigger>
      <DrawerPortal>
        <DrawerOverlay className="fixed inset-0 bg-black/40 z-[9997]" />
        <DrawerContent className="fixed inset-x-0 bottom-0 z-[9998] h-[90vh] px-4 pb-8 bg-background rounded-t-[10px]">
          <div className="flex items-center justify-between py-4 border-b border-border">
            <DrawerClose asChild>
              <button className="p-2 hover:bg-muted rounded-full">
                <X className="w-5 h-5" />
              </button>
            </DrawerClose>
            <span className="font-semibold">Search</span>
            <button onClick={handleClear} className="text-sm text-muted-foreground">
              Clear
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-6 space-y-6">
            {/* Where Section */}
            <div className="space-y-3">
              <label className="text-sm font-semibold">Where</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search destinations"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-border rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              
              {/* Quick Locations */}
              <div className="space-y-2">
                <button 
                  onClick={handleNearbyClick}
                  disabled={nearbyLoading}
                  className="flex items-center gap-3 w-full p-3 hover:bg-muted rounded-xl transition-colors disabled:opacity-50"
                >
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    {nearbyLoading ? (
                      <Loader2 className="w-5 h-5 text-primary animate-spin" />
                    ) : (
                      <Navigation className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div className="text-left">
                    <span className="font-medium">{nearbyLoading ? 'Finding location...' : 'Nearby'}</span>
                    {userCity && <p className="text-xs text-muted-foreground">Based on {userCity}</p>}
                  </div>
                </button>
                {locations.slice(0, 5).map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => setLocation(loc.name)}
                    className={cn(
                      "flex items-center gap-3 w-full p-3 hover:bg-muted rounded-xl transition-colors",
                      location === loc.name && "bg-muted"
                    )}
                  >
                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <span className="font-medium">{loc.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* When Section */}
            <div className="space-y-3">
              <label className="text-sm font-semibold">When</label>
              <button
                onClick={() => setShowCalendar(!showCalendar)}
                className={cn(
                  "flex items-center gap-3 w-full p-4 border border-border rounded-xl",
                  showCalendar && "ring-2 ring-primary"
                )}
              >
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <span className={date ? "text-foreground" : "text-muted-foreground"}>
                  {date ? format(date, "MMM d, yyyy") : "Any date"}
                </span>
              </button>
              {showCalendar && (
                <div className="border border-border rounded-xl p-2 bg-background">
                  <CalendarComponent
                    mode="single"
                    selected={date}
                    onSelect={(d) => { setDate(d); setShowCalendar(false); }}
                    className="pointer-events-auto"
                  />
                </div>
              )}
            </div>

            {/* What Section */}
            <div className="space-y-3">
              <label className="text-sm font-semibold">What</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'all', label: 'All' },
                  { value: 'events', label: 'Events' },
                  { value: 'experiences', label: 'Experiences' }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => { setListingType(type.value); setSelectedCategory(null); }}
                    className={cn(
                      "py-3 px-4 rounded-xl border text-sm font-medium transition-all",
                      listingType === type.value
                        ? "bg-foreground text-background border-foreground"
                        : "border-border hover:border-foreground"
                    )}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              {/* Category Selection */}
              {categories.length > 0 && (
                <div className="mt-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                    Categories
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-sm transition-all",
                          selectedCategory === cat
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted hover:bg-muted/80"
                        )}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search Button */}
          <Button
            onClick={handleSearch}
            className="w-full py-6 text-base font-semibold rounded-xl"
            size="lg"
          >
            <Search className="w-5 h-5 mr-2" />
            Search
          </Button>
        </DrawerContent>
      </DrawerPortal>
    </Drawer>
  );
};